import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import { Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import Card from 'tailwind-components/Card/Card';
import { useApiQuery } from 'lib/api';
import { parseDashboardForPayeeResponse } from 'helpers/dashboard/dashboardForPayee';
import CardMessage from './CardMessage';
import CommissionsMonthTile from './CommissionsMonthTile';
import MonthTilesSkeleton from './MonthTilesSkeleton';
import useCommissionMonthDownload from './useCommissionMonthDownload';
import {
  dashboardForPayeeKeys,
  dashboardForPayeeRequest
} from './api/dashboardForPayee';

const MONTHS_SHOWN = 4;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// turns the api's 'YYYY-MM' period into 'August 2026', falling back to the raw value
function formatPeriodLabel(period) {
  if (typeof period !== 'string') return '';
  const [year, month] = period.split('-');
  const monthName = MONTH_NAMES[Number(month) - 1];
  if (!year || !monthName) return period;
  return `${monthName} ${year}`;
}

/**
 * last months of commission payouts, read from the same dashboard-for-payee
 * endpoint that feeds the commission insights panel; each month runs its own
 * statement when clicked
 * @param user - zoho data of the logged agent; its id identifies the payee
 */
export default function CommissionsByMonthCard({ user }) {
  const payeeId = String(user?.id ?? '').trim();
  const request = dashboardForPayeeRequest(payeeId);
  // without a payee or a route for the active company there is nothing to ask for
  const enabled = Boolean(payeeId && request.path);

  const { data, isPending, isError } = useApiQuery(
    dashboardForPayeeKeys.byAgent(payeeId),
    request,
    { enabled }
  );

  const { buildMonthDownload, isDownloading } =
    useCommissionMonthDownload(payeeId);

  const months = useMemo(() => {
    const parsed = parseDashboardForPayeeResponse(data);
    const points = parsed?.commissionsTrend?.points ?? [];
    // the api returns the trend oldest first, the card shows the latest months first
    return [...points].reverse().slice(0, MONTHS_SHOWN);
  }, [data]);

  function renderBody() {
    if (enabled && isPending) return <MonthTilesSkeleton count={MONTHS_SHOWN} />;
    if (isError) {
      return <CardMessage>Unable to load your commissions.</CardMessage>;
    }
    if (months.length === 0) {
      return <CardMessage>No commissions paid yet.</CardMessage>;
    }
    return (
      <div className='grid grid-cols-2 gap-2.5 lg:grid-cols-4'>
        {months.map(point => (
          <CommissionsMonthTile
            key={point.period}
            label={formatPeriodLabel(point.period)}
            amount={point.value}
            disabled={isDownloading}
            onDownload={buildMonthDownload(point.period)}
          />
        ))}
      </div>
    );
  }

  return (
    <Card
      variant='elevated'
      title='Commissions by month'
      icon={<Wallet aria-hidden />}
      action={
        <Link
          to='/commissions/statements'
          className='shrink-0 text-xs font-semibold text-primary-emphasis-light hover:underline'
        >
          View statements →
        </Link>
      }
    >
      {renderBody()}
    </Card>
  );
}

CommissionsByMonthCard.propTypes = {
  user: propTypes.object.isRequired
};
